import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  message,
  Input,
  Button,
  Table,
  Modal,
  Spin,
  Tag,
  Descriptions,
  Typography,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import VehiclesForm from "./vehiclesForm";
import axios from "axios";
import { useHistory, NavLink } from "react-router-dom";

const { Search } = Input;
const { Title } = Typography;

const Vehicles = () => {
  const [irModalVisible, setProjectModalVisible] = useState(false);
  const [formData, setFormData] = useState({});
  const [vehiclesData, setVehiclesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const history = useHistory();

  useEffect(() => {
    fetchData();
  }, [submitted]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get("http://93.127.203.113:5005/vehicles");
      setVehiclesData(response.data);
      setFilteredData(response.data);
    } catch (error) {
      message.error("Unable to load data!");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    const filteredData = vehiclesData.filter((vehicle) =>
      Object.keys(vehicle).some((key) =>
        String(vehicle[key]).toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredData(filteredData);
  };

  const handleAddVehicle = () => {
    setFormData({});
    setProjectModalVisible(true);
  };

  const handleEditVehicle = (record) => {
    setFormData(record);
    setProjectModalVisible(true);
  };

  const handleCloseDetails = () => {
    setSelectedRow(null);
  };

  const highlightText = (text, searchText) => {
    // Return "N/A" if the text is null or undefined
    if (text == null) {
      return "N/A";
    }
    if (!searchText) return text;
  
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = text.toString().split(regex);
  
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "#fffb8f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };
  

  const columns = [
    {
      title: "Plate Number",
      dataIndex: "plateNumber",
      key: "plateNumber",
      render: (text) => highlightText(text, searchQuery),
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      render: (text) => highlightText(text, searchQuery),
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      render: (text) => highlightText(text, searchQuery),
    },
    {
      title: "Vehicle Type",
      dataIndex: ["vehicle_type", "typeName"],
      key: "vehicleType",
      render: (_, record) =>
        record.vehicle_type
          ? highlightText(record.vehicle_type.typeName, searchQuery)
          : "N/A",
    },
    {
      title: "Driver",
      dataIndex: ["driver", "firstName"],
      key: "driver",
      render: (_, record) =>
        record.driver
          ? highlightText(
              `${record.driver.firstName} ${record.driver.lastName}`,
              searchQuery
            )
          : "N/A",
    },
    {
      title: "Owner",
      dataIndex: ["owner", "name"],
      key: "owner",
      render: (_, record) =>
        record.owner
          ? highlightText(record.owner.name, searchQuery)
          : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const displayStatus = status ?? "Active"; // Default to "Active" if null/undefined
        return (
          <Tag color={displayStatus === "Active" ? "green" : "red"}>
            {highlightText(displayStatus, searchQuery)}
          </Tag>
        );
      },
    },
    
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditVehicle(record)}
            style={{ marginRight: 8, color: "#0c8c49" }}
          >
            Edit
          </Button>
          <Button type="link">
            <NavLink
              to={`/vehicleDetails/${record.id}`}
              style={{ color: "#0c8c49" }}
            >
              <InfoCircleOutlined /> Details
            </NavLink>
          </Button>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Card
        style={{
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderTop: "5px solid #0c8c49",
        }}
      >
        <Title level={4} style={{ color: "#0c8c49", marginBottom: "20px" }}>
          Vehicles
        </Title>
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          <Col span={12}>
            <Search
              placeholder="Search Vehicles"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddVehicle}
              style={{
                backgroundColor: "#0c8c49",
                borderColor: "#0c8c49",
              }}
            >
              Add Vehicle
            </Button>
          </Col>
        </Row>
        {loading ? (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 5 }}
            rowKey="id"
            bordered
          />
        )}
      </Card>

      {selectedRow && (
        <Card
          title={`Details of ${selectedRow.plateNumber}`}
          bordered={false}
          style={{
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            marginTop: "20px",
          }}
          extra={
            <Button
              type="link"
              onClick={handleCloseDetails}
              style={{ color: "red" }}
            >
              Close
            </Button>
          }
        >
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Plate Number">
              {selectedRow.plateNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Model">
              {selectedRow.model}
            </Descriptions.Item>
            <Descriptions.Item label="Color">
              {selectedRow.color}
            </Descriptions.Item>
            <Descriptions.Item label="Vehicle Type">
              {selectedRow.vehicle_type?.typeName || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Driver">
              {selectedRow.driver
                ? `${selectedRow.driver.firstName} ${selectedRow.driver.lastName}`
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Owner">
              {selectedRow.owner?.name || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {selectedRow.status || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Modal
        title={formData.id ? "Edit Vehicle" : "Add Vehicle"}
        visible={irModalVisible}
        onCancel={() => setProjectModalVisible(false)}
        footer={null}
        centered
        width="50%"
        style={{ borderRadius: "10px" }}
        bodyStyle={{
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <VehiclesForm
          formData={formData}
          setFormData={setFormData}
          closeModal={() => setProjectModalVisible(false)}
          setSubmitted={setSubmitted}
        />
      </Modal>
    </div>
  );
};

export default Vehicles;
